import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next"; // For localization
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Paper,
  Modal,
  Backdrop,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle"; // MUI icon
import CloseIcon from "@mui/icons-material/Close";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import { motion } from "framer-motion";
import styled from "styled-components";
import leaseBusinessDiagram from "images/business/lease-business.png";
import ContactMailIcon from "@mui/icons-material/ContactMail"; // MUI icon for header
import BgTitle from "components/title/BgTitle";

// Styled components for modal
const FullscreenImage = styled(Box)`
  position: relative;
  height: 90vh;
  width: 90vw;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const ImageHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: #f1f5f9;
  border-bottom: 1px solid #e5e7eb;
`;

const ImageContent = styled(Box)`
  flex: 1;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8fafc;
  padding: 24px;
`;

const LeaseBusinessMobile = ({ isXs = false }) => {
  const myRef = useRef();
  const { t } = useTranslation(); // Initialize translation hook
  const theme = useTheme();
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const openLightbox = () => setLightboxOpen(true);
  const closeLightbox = () => setLightboxOpen(false);

  return (
    <Box
      ref={myRef}
      id="lease-business"
      sx={{
        borderRadius: 2, // Rounded corners
        textAlign: "left", // Left-align text
        mt: 4,
      }}
    >
      {/* Heading */}
      <BgTitle text={t("business.lease-of-pc.heading")} isCenter={true} />

      {/* Image with Zoom */}
      <Box
        sx={{
          textAlign: "center", // Center-align image
          mt: 1,
          position: "relative",
        }}
      >
        <Box
          onClick={openLightbox}
          sx={{
            position: "relative",
            display: "inline-block",
            cursor: "pointer",
            "&:hover": {
              "& .zoom-overlay": {
                opacity: 1,
              },
              "& .zoom-hint": {
                opacity: 1,
              },
            },
          }}
        >
          <Box
            component="img"
            src={leaseBusinessDiagram}
            alt="Lease Business Diagram"
            sx={{
              width: "100%",
              maxWidth: "300px", // Limit image size
              borderRadius: 2, // Rounded corners
              boxShadow: 3, // Add shadow
              margin: "auto", // Center image
              transition: "transform 0.3s ease",
              "&:hover": {
                transform: "scale(1.02)",
              },
            }}
          />
          {/* Zoom Icon Overlay */}
          <Box
            className="zoom-overlay"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              opacity: 0,
              transition: "opacity 0.3s ease",
              borderRadius: 2,
            }}
          >
            <ZoomOutMapIcon sx={{ fontSize: 48, color: "white" }} />
          </Box>
          {/* Zoom Hint Badge */}
          <Box
            className="zoom-hint"
            sx={{
              position: "absolute",
              bottom: "8px",
              right: "8px",
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              px: 2,
              py: 0.5,
              borderRadius: "20px",
              fontSize: "0.75rem",
              fontWeight: 500,
              color: "#374151",
              boxShadow: 2,
              opacity: 0,
              transition: "opacity 0.3s ease",
              pointerEvents: "none",
            }}
          >
            🔍 Click to zoom
          </Box>
        </Box>
      </Box>

      {/* Description */}
      <Box
        sx={{
          textAlign: "left",
          borderLeft: 2,
          borderColor: "primary.main",
          padding: 1,
          mt: 2,
          bgcolor: "#f5f5f5",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary, // Use secondary text color
            mb: 1,
            fontSize: "0.8rem",
          }}
        >
          {t("business.lease-of-pc.description")}
        </Typography>
      </Box>

      {/* Feature List */}
      <List>
        {t("business.lease-of-pc.content", { returnObjects: true }).map(
          (feature, index) => (
            <ListItem
              key={index}
              sx={{ padding: 0, marginBottom: theme.spacing(1) }}
            >
              <ListItemIcon sx={{ minWidth: "32px" }}>
                <CheckCircleIcon sx={{ color: theme.palette.primary.main }} />
              </ListItemIcon>
              <ListItemText
                primary={feature}
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
              />
            </ListItem>
          )
        )}
      </List>

      {/* Contact Section */}
      <Box
        component={Paper}
        variant="outlined"
        sx={{
          marginTop: theme.spacing(2),
          backgroundColor: "#ffffff", // White background
          borderRadius: 2, // Rounded corners
          overflow: "hidden", // Ensure border radius applies to children
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: theme.spacing(2),
            backgroundColor: "#f5f5f5",
          }}
        >
          <ContactMailIcon sx={{ marginRight: theme.spacing(1) }} />{" "}
          {/* Icon */}
          <Typography variant="h6" component="h3" sx={{ fontWeight: "bold" }}>
            Contact
          </Typography>
        </Box>

        {/* Body */}
        <Box
          sx={{
            padding: theme.spacing(2),
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.primary,
            }}
          >
            {t("business.lease-of-pc.contact")}
          </Typography>
        </Box>
      </Box>

      {/* Professional Modal for Image Zoom */}
      <Modal
        open={lightboxOpen}
        onClose={closeLightbox}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          onClick: closeLightbox,
          style: { backgroundColor: "rgba(0, 0, 0, 0.85)" },
        }}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          style={{ outline: "none", maxWidth: "95vw", maxHeight: "95vh" }}
        >
          <FullscreenImage>
            <ImageHeader>
              <Typography
                variant="h6"
                component="h3"
                style={{ fontWeight: 600, color: "#1e293b" }}
              >
                {t("business.lease-of-pc.heading")}
              </Typography>
              <IconButton
                onClick={closeLightbox}
                size="small"
                style={{
                  backgroundColor: "#f1f5f9",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "rotate(90deg)";
                  e.currentTarget.style.backgroundColor = "#e2e8f0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "rotate(0deg)";
                  e.currentTarget.style.backgroundColor = "#f1f5f9";
                }}
              >
                <CloseIcon />
              </IconButton>
            </ImageHeader>
            <ImageContent>
              <img
                src={leaseBusinessDiagram}
                alt="Lease Business Diagram - Full Size"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  borderRadius: "4px",
                }}
              />
            </ImageContent>
          </FullscreenImage>
        </motion.div>
      </Modal>
    </Box>
  );
};

export default LeaseBusinessMobile;
